import {
  Id,
  ToastContent,
  ToastItemProps,
} from '../components/ToastItem/ToastItem.types';
import { ContainerInstance } from '../hooks/useToastContainer';

export const enum Event {
  Show,
  Clear,
  DidMount,
  WillUnmount,
}

type OnShowCallback = (content: ToastContent, options: ToastItemProps) => void;
type OnClearCallback = (id?: Id) => void;
type OnDidMountCallback = (containerInstance: ContainerInstance) => void;
type OnWillUnmountCallback = OnDidMountCallback;

type Callback =
  | OnShowCallback
  | OnClearCallback
  | OnDidMountCallback
  | OnWillUnmountCallback;
type TimeoutId = ReturnType<typeof setTimeout>;

export type EventManager = {
  list: Map<Event, Callback[]>;
  emitQueue: Map<Event, TimeoutId[]>;
  on(event: Event.Show, callback: OnShowCallback): EventManager;
  on(event: Event.Clear, callback: OnClearCallback): EventManager;
  on(event: Event.DidMount, callback: OnDidMountCallback): EventManager;
  on(event: Event.WillUnmount, callback: OnWillUnmountCallback): EventManager;
  off(event: Event, callback?: Callback): EventManager;
  cancelEmit(event: Event): EventManager;
  emit(
    event: Event.Show,
    content: React.ReactNode,
    options: ToastItemProps,
  ): void;
  emit(event: Event.Clear, id?: string | number): void;
  emit(event: Event.DidMount, containerInstance: ContainerInstance): void;
  emit(event: Event.WillUnmount, containerInstance: ContainerInstance): void;
};

export const eventManager: EventManager = {
  list: new Map(),
  emitQueue: new Map(),

  on(event: Event, callback: Callback) {
    this.list.has(event) || this.list.set(event, []);
    this.list.get(event)!.push(callback);
    return this;
  },

  off(event, callback) {
    if (callback) {
      const cb = this.list.get(event)!.filter(c => c !== callback);
      this.list.set(event, cb);
      return this;
    }
    this.list.delete(event);
    return this;
  },

  cancelEmit(event) {
    const timers = this.emitQueue.get(event);
    if (timers) {
      timers.forEach(clearTimeout);
      this.emitQueue.delete(event);
    }

    return this;
  },

  emit(event: Event, ...args: any[]) {
    this.list.has(event) &&
      this.list.get(event)!.forEach((callback: Callback) => {
        if (process.env.NODE_ENV === 'test') {
          // @ts-ignore
          callback(...args);
          return;
        }

        const timer: TimeoutId = setTimeout(() => {
          // @ts-ignore
          callback(...args);
        }, 0);

        this.emitQueue.has(event) || this.emitQueue.set(event, []);
        this.emitQueue.get(event)!.push(timer);
      });
  },
};
